import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Badge } from 'primereact/badge';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { useOrderData } from '../hooks/useOrderData';
import { useLineItems } from '../hooks/useLineItems';
import { ShippingLineItem } from '../DeShippingTableWrapper';
import { LineItemStatusBadge } from './LineItemStatusBadge';
import { LineItemAssignmentDialog } from './dialogs/LineItemAssignmentDialog';
import { useLocationAssignment } from '../hooks/useLocationAssignment';
import { LineItemFulfillmentLocationTemplate } from './shared/LineItemFulfillmentLocationTemplate';
import { useOrderSelection } from '../hooks/useOrderSelection';
import { useLineItemSelection } from '../hooks/useLineItemSelection';

interface RejectedItemsDialogProps {
  orderData: ReturnType<typeof useOrderData>;
  orderSelection: ReturnType<typeof useOrderSelection>;
  lineItemsHook: ReturnType<typeof useLineItems>;
  lineItemSelection: ReturnType<typeof useLineItemSelection>;
  visible: boolean;
  onHide: () => void;
}

export const RejectedItemsDialog: React.FC<RejectedItemsDialogProps> = ({
  orderData,
  orderSelection,
  lineItemsHook,
  lineItemSelection,
  visible,
  onHide,
}) => { 
  const order = orderSelection.selectedOrder;
  const {
    lineItems,
    loading,
    error,
    refetch: fetchLineItems,
    setSelectedLineItem,
    updating,
    reacceptLineItem,
    statusUpdating,
  } = lineItemsHook;

  const locationAssignmentHook = useLocationAssignment();

  const toastRef = React.useRef<Toast>(null);
  
  // State for re-accept confirmation dialog
  const [showReAcceptDialog, setShowReAcceptDialog] = React.useState(false);
  const [itemToReAccept, setItemToReAccept] = React.useState<ShippingLineItem | null>(null);

  const handleReassignStore = (lineItem: ShippingLineItem) => {
    setSelectedLineItem(lineItem);
    locationAssignmentHook.showDialog(lineItem);
  };

  const handleReAccept = (lineItem: ShippingLineItem) => {
    setItemToReAccept(lineItem);
    setShowReAcceptDialog(true);
  };

  const confirmReAccept = async () => {
    if (!itemToReAccept) return;

    try {
      // Set the selected line item for the hook
      setSelectedLineItem(itemToReAccept);
      
      // Call the accept function
      const result = await reacceptLineItem(itemToReAccept);
      
      if (result.success) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `Successfully re-accepted "${itemToReAccept.product_name}"`,
          life: 3000
        });
        
        // Refresh the line items data
        fetchLineItems();
        orderData.refreshSingleOrder(itemToReAccept.order_id.toString());
      } else {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.message || 'Failed to re-accept item',
          life: 5000
        });
      }
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'An unexpected error occurred while re-accepting the item',
        life: 5000
      });
    } finally {
      setShowReAcceptDialog(false);
      setItemToReAccept(null);
    }
  };

  const cancelReAccept = () => {
    setShowReAcceptDialog(false);
    setItemToReAccept(null);
  };

  const actionTemplate = (rowData: ShippingLineItem) => {
    const isStoreDeclined = rowData.fulfillment_status === 'store_declined_order';
    const isAdminRejected = rowData.fulfillment_status === 'rejected_by_admin';
    
    if (!isStoreDeclined && !isAdminRejected) {
      return null;
    }

    return (
      <div className="flex gap-2">
        {isAdminRejected && (
          <Button
            label="Re-accept"
            icon="pi pi-check"
            size="small"
            onClick={() => handleReAccept(rowData)}
            disabled={updating || statusUpdating}
            className="p-button-success p-button-outlined"
          />
        )}
        <Button
          label="Reassign to Different Store"
          icon="pi pi-shop"
          size="small"
          onClick={() => handleReassignStore(rowData)}
          disabled={updating || statusUpdating}
          className="p-button-outlined"
        />
      </div>
    );
  };

  const declineReasonTemplate = (rowData: ShippingLineItem) => {
    return rowData.decline_reason || '';
  };

  const getRejectionStats = () => {
    const storeDeclined = lineItems.filter(item => item.fulfillment_status === 'store_declined_order').length;
    const adminRejected = lineItems.filter(item => item.fulfillment_status === 'rejected_by_admin').length;
    const totalRejected = storeDeclined + adminRejected;
    return { storeDeclined, adminRejected, totalRejected };
  };

  const stats = getRejectionStats();

  const renderContent = () => {
    // Loading state
    if (loading) {
      return (
        <div>
          <div className="mb-4">
            <Skeleton width="200px" height="1.5rem" className="mb-3" />
            <div className="flex gap-4 mb-3">
              <div className="flex align-items-center gap-2 py-2">
                <Skeleton width="30px" height="1.5rem" borderRadius="16px" />
                <Skeleton width="140px" height="1rem" />
              </div>
              <div className="flex align-items-center gap-2 py-2">
                <Skeleton width="30px" height="1.5rem" borderRadius="16px" />
                <Skeleton width="140px" height="1rem" />
              </div>
              <div className="flex align-items-center gap-2 py-2">
                <Skeleton width="30px" height="1.5rem" borderRadius="16px" />
                <Skeleton width="120px" height="1rem" />
              </div>
            </div>
          </div>

          <div>
            <Skeleton width="100px" height="1.25rem" className="mb-3" />
            <div className="border-1 surface-border border-round">
              <div className="p-3 border-bottom-1 surface-border">
                <div className="flex gap-4">
                  <Skeleton width="120px" height="1rem" />
                  <Skeleton width="80px" height="1rem" />
                  <Skeleton width="60px" height="1rem" />
                  <Skeleton width="80px" height="1rem" />
                  <Skeleton width="140px" height="1rem" />
                  <Skeleton width="200px" height="1rem" />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className="flex flex-column gap-4">
          <Message 
            severity="error" 
            text={`Failed to load line items: ${error}`}
            className="w-full"
          />
          <div className="flex justify-content-center">
            <Button
              label="Retry"
              icon="pi pi-refresh"
              onClick={fetchLineItems}
              className="p-button-outlined"
            />
          </div>
        </div>
      );
    }

    // Empty state
    if (lineItems.length === 0) {
      return (
        <div className="flex flex-column align-items-center justify-content-center p-6">
          <i className="pi pi-inbox" style={{ fontSize: '3rem', color: 'var(--text-color-secondary)' }}></i>
          <h6 className="mt-3 mb-2">No Line Items Found</h6>
          <p className="m-0">This order doesn't have any line items.</p>
        </div>
      );
    }

    // Success state with data
    return (
      <div>
        <div className="mb-4">
          <h5>Order: {order?.order_name}</h5>
          <div className="flex gap-4 mb-3">
            <div className="flex align-items-center gap-2 py-2">
              <Badge value={stats.storeDeclined} severity="warning" />
              <span className="font-bold px-2">Store Declined Items</span>
            </div>
            <div className="flex align-items-center gap-2 py-2">
              <Badge value={stats.adminRejected} severity="danger" />
              <span className="font-bold px-2">Admin Rejected Items</span>
            </div>
            <div className="flex align-items-center gap-2 py-2">
              <Badge value={stats.totalRejected} severity="info" />
              <span className="font-bold px-2">Total Rejected Items</span>
            </div>
          </div>
          {(updating || statusUpdating) && (
            <Message 
              severity="info" 
              text="Updating line items..." 
              className="w-full mb-3"
            />
          )}
        </div>

        <div>
          <h6 className="mb-3">Line Items</h6>
          <DataTable 
            value={lineItems} 
            responsiveLayout="scroll"
            paginator
            rows={10}
            emptyMessage="No line items found"
            loading={updating || statusUpdating}
          >
            <Column 
              field="product_name" 
              header="Product Name" 
              sortable
            />
            <Column 
              field="variant_title" 
              header="Variant" 
            />
            <Column 
              field="product_quantity" 
              header="Quantity" 
              style={{ width: '100px' }}
            />
            <Column 
              header="Status" 
              body={(rowData: ShippingLineItem) => <LineItemStatusBadge status={rowData.fulfillment_status || 'unassigned'} />}
              style={{ width: '120px' }}
            />
            <Column 
              header="Decline/Rejection Reason" 
              body={declineReasonTemplate}
              style={{ width: '200px' }}
            />
            <Column 
              header="Location" 
              body={(rowData: ShippingLineItem) => <LineItemFulfillmentLocationTemplate {...rowData} />}
              style={{ width: '150px' }}
            />
            <Column 
              header="Action" 
              body={actionTemplate}
              style={{ width: '300px' }}
            />
          </DataTable>
        </div>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        header="Handle Rejected Items"
        visible={visible}
        onHide={onHide}
        style={{ width: '1100px' }}
        modal
        data-testid="handle-rejected-items-dialog"
        footer={
          <div>
            <Button 
              label="Refresh" 
              icon="pi pi-refresh" 
              onClick={fetchLineItems}
              className="p-button-outlined mr-2"
              disabled={loading || updating || statusUpdating}
            />
            <Button 
              label="Close" 
              icon="pi pi-times" 
              onClick={onHide} 
              className="p-button-text" 
              disabled={updating || statusUpdating}
            />
          </div>
        }
      >
        {order ? renderContent() : (
          <Message 
            severity="warn" 
            text="No order selected" 
            className="w-full"
          />
        )}
      </Dialog>

      {/* Re-accept Confirmation Dialog */}
      <Dialog
        header="Confirm Re-accept Item"
        visible={showReAcceptDialog}
        onHide={cancelReAccept}
        style={{ width: '500px' }}
        modal
        footer={
          <div>
            <Button 
              label="Cancel" 
              icon="pi pi-times" 
              onClick={cancelReAccept}
              className="p-button-text mr-2"
              disabled={statusUpdating}
            />
            <Button 
              label="Re-accept Item" 
              icon="pi pi-check" 
              onClick={confirmReAccept}
              className="p-button-success"
              disabled={statusUpdating}
              loading={statusUpdating}
            />
          </div>
        }
      >
        {itemToReAccept && (
          <div className="flex flex-column gap-3">
            <Message 
              severity="info" 
              text="Are you sure you want to re-accept this previously rejected item?" 
              className="w-full"
            />
            
            <div className="p-3 surface-100 border-round">
              <div className="flex flex-column gap-2">
                <div><strong>Product:</strong> {itemToReAccept.product_name}</div>
                <div><strong>Quantity:</strong> {itemToReAccept.product_quantity}</div>
                <div><strong>Current Status:</strong> <LineItemStatusBadge status={itemToReAccept.fulfillment_status || 'unassigned'} /></div>
                <div><strong>Current Rejection Reason:</strong> {itemToReAccept.decline_reason || 'No reason provided'}</div>
              </div>
            </div>
            
            <p className="m-0">
              This action will change the item's status back to 'Awaiting Shipment' and it will be available for shipping by the POS store again.
            </p>
          </div>
        )}
      </Dialog>

      <LineItemAssignmentDialog
        orderData={orderData}
        orderSelection={orderSelection}
        lineItemsHook={lineItemsHook}
        locationAssignmentHook={locationAssignmentHook}
        lineItemSelection={lineItemSelection}
      />
    </>
  );
};